// OCT-7341: TreatyApp: File Activity panel — add search, filters, and a count of matching activities - Frontend : ashish.atwal@resolutionlife.com
// Loads data-room activity events for the Activity panel. Event-type filtering
// (and all the new user/folder/file/search filtering) is now applied client-side
// over the loaded events, so this hook no longer sends an event-type filter to
// the server — it loads the recent activity and lets the panel filter in memory.
// "Show more" cursor pagination is unchanged and appends older activity.
import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useSplunkLogger } from '@resolutionlife/ui';

import * as ToastConfigs from '../../../configs/toast';
import {
  EventFragment,
  EventType,
  useGetFileActivityEventsQuery,
} from '../../../graphql/generated';

export type FileActivityServerFilter = {
  eventType?: EventType;
  user?: string;
  folder?: string;
  file?: string;
  search?: string;
};

type UseFileActivityEventsBag = {
  error: boolean;
  loading: boolean;
  retry: () => void;
  hasMore?: boolean;
  cedantName?: string;
  isFetchingMore: boolean;
  events?: EventFragment[];
  fetchMore: (cursor: string) => () => void;
};

export const useFileActivityEvents = (
  cedant: string,
  filter: FileActivityServerFilter = {}
): UseFileActivityEventsBag => {
  const toast = useToast();
  const splunkLogger = useSplunkLogger();
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);

  const emptyToUndefined = (value?: string) =>
    value?.trim() ? value : undefined;

  const {
    error,
    loading,
    data,
    refetch,
    fetchMore,
  } = useGetFileActivityEventsQuery({
    variables: {
      cedant,
      eventTypes: filter.eventType ? [filter.eventType] : undefined,
      user: emptyToUndefined(filter.user),
      folder: emptyToUndefined(filter.folder),
      file: emptyToUndefined(filter.file),
      search: emptyToUndefined(filter.search),
    },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });

  const updateQuery = (prev: any, { fetchMoreResult }: any) => {
    if (!fetchMoreResult?.cedant?.events?.events || !prev.cedant) return prev;
    return {
      ...prev,
      cedant: {
        ...prev.cedant,
        __typename: "CedantDetails",
        events: {
          ...prev.cedant.events,
          __typename: "EventsResponse",
          events: [...(prev.cedant.events?.events || []), ...fetchMoreResult.cedant.events.events],
          hasMore: fetchMoreResult.cedant.events.hasMore,
        },
      },
    };
  };

  const handleFetchMore = (cursor: string) => async () => {
    try {
      setIsFetchingMore(true);
      await fetchMore({
        variables: {
          cedant,
          eventTypes: filter.eventType ? [filter.eventType] : undefined,
          user: emptyToUndefined(filter.user),
          folder: emptyToUndefined(filter.folder),
          file: emptyToUndefined(filter.file),
          search: emptyToUndefined(filter.search),
          pagination: { cursor },
        },
        updateQuery,
      });
    } catch (error) {
      console.error('FetchMore error:', error);
      toast(ToastConfigs.problemCompletingRequest);
      splunkLogger.error('Activity feed pagination failed', { eventCategory: 'activity_feed_error', cedant, errorMessage: String(error) });
    } finally {
      setIsFetchingMore(false);
    }
  };

  return {
    loading,
    error: !!error,
    retry: () => { refetch(); },
    isFetchingMore,
    fetchMore: handleFetchMore,
    cedantName: data?.cedant?.name,
    events: data?.cedant?.events.events,
    hasMore: data?.cedant?.events.hasMore,
  };
};
